import { supabase, handleSupabaseError } from '@/lib/supabase';
import { UserRole } from '@/types/enums';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  fullName: string;
  role: UserRole;
  branchId?: string;
  phoneNumber?: string;
  address?: string;
}

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  isActive: boolean;
}

class AuthService {
  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<{ user: AuthUser; error: string | null }> {
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password
      });

      if (authError) {
        // Catat percobaan gagal (mis. password salah) untuk "Percobaan Login
        // Gagal" di detail pengguna. Best-effort: kegagalan RPC ini tidak
        // boleh menutupi pesan error login yang sebenarnya.
        await this.recordFailedLogin(credentials.email);
        throw authError;
      }
      if (!authData.user) throw new Error('Login gagal');

      // Fetch user profile
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, full_name, role, is_active')
        .eq('id', authData.user.id)
        .single();

      if (userError) throw userError;

      // Log activity + catat IP/perangkat/last_login_at (RPC migrasi 0022 & 0040).
      await this.logActivity(userData.id, 'LOGIN', 'Login berhasil');
      await this.updateLoginTracking();

      return {
        user: {
          id: userData.id,
          email: userData.email,
          fullName: userData.full_name,
          role: userData.role as UserRole,
          isActive: userData.is_active
        },
        error: null
      };
    } catch (error) {
      return {
        user: null as any,
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Login user with Google OAuth
   */
  async loginWithGoogle(): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Registrasi gagal');

      // Create user profile
      const { data: userData, error: userError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          email: data.email,
          full_name: data.fullName,
          role: data.role,
          is_active: data.role === UserRole.NASABAH ? false : true // Nasabah needs approval
        })
        .select()
        .single();

      if (userError) throw userError;

      // If nasabah, create profile.
      // Idempoten: trigger DB (handle_new_user) mungkin sudah membuat baris
      // profil (branch_id NULL) saat signUp. Jika sudah ada, kita UPDATE baris
      // itu alih-alih INSERT baris kedua — mencegah duplikat profil per user.
      if (data.role === UserRole.NASABAH && data.branchId) {
        const { data: existing, error: existErr } = await supabase
          .from('nasabah_profiles')
          .select('id')
          .eq('user_id', userData.id)
          .limit(1);

        if (existErr) throw existErr;

        if (existing && existing.length > 0) {
          const { error: updateError } = await supabase
            .from('nasabah_profiles')
            .update({
              branch_id: data.branchId,
              phone_number: data.phoneNumber,
              address: data.address,
              status: 'PENDING'
            })
            .eq('id', existing[0].id);

          if (updateError) throw updateError;
        } else {
          // account_number tidak di-set: trigger DB (migrasi 0052) menerbitkan
          // nomor 12 digit berprefix bank sampah karena branch_id terisi.
          const { error: profileError } = await supabase
            .from('nasabah_profiles')
            .insert({
              user_id: userData.id,
              branch_id: data.branchId,
              phone_number: data.phoneNumber,
              address: data.address,
              status: 'PENDING'
            });

          if (profileError) throw profileError;
        }
      }

      // If pengelola, create profile
      if (data.role === UserRole.PENGELOLA && data.branchId) {
        const { error: profileError } = await supabase
          .from('pengelola_profiles')
          .insert({
            user_id: userData.id,
            branch_id: data.branchId
          });

        if (profileError) throw profileError;
      }

      return {
        user: {
          id: userData.id,
          email: userData.email,
          fullName: userData.full_name,
          role: userData.role as UserRole,
          isActive: userData.is_active
        },
        error: null
      };
    } catch (error) {
      return {
        user: null,
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Ubah password akun.
   *
   * Supabase `updateUser` TIDAK memverifikasi password lama, jadi kita verifikasi
   * dulu dengan mencoba sign-in memakai password lama (email pengguna saat ini),
   * baru kemudian memperbarui password. Mencegah perubahan oleh sesi yang
   * "ditinggalkan" tanpa tahu password lama.
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ error: string | null }> {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser?.email) {
        return { error: 'Sesi tidak valid. Silakan login ulang.' };
      }

      // Verifikasi password lama.
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: authUser.email,
        password: currentPassword,
      });
      if (verifyError) {
        return { error: 'Password lama salah.' };
      }

      // Update ke password baru.
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (updateError) throw updateError;

      await this.logActivity(authUser.id, 'PASSWORD_CHANGE', 'Password akun berhasil diubah');

      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Verifikasi password akun pengguna saat ini (untuk alur "Lupa PIN").
   * Memakai signInWithPassword yang diverifikasi server; mengembalikan true bila benar.
   */
  async verifyPassword(password: string): Promise<boolean> {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser?.email) return false;
      const { error } = await supabase.auth.signInWithPassword({
        email: authUser.email,
        password,
      });
      return !error;
    } catch {
      return false;
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      // `getSession()` (bukan `getUser()`) dipakai di sini karena tidak melempar
      // AuthSessionMissingError saat belum ada sesi (mis. pengunjung pertama kali
      // membuka halaman login) — kondisi tsb normal, bukan error.
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) throw sessionError;

      const authUser = session?.user ?? null;
      if (!authUser) return { user: null, error: null };

      // Try to get user from users table
      let { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, full_name, role, is_active')
        .eq('id', authUser.id)
        .single();

      // If user doesn't exist in users table (first time Google login), create it
      if (userError && userError.code === 'PGRST116') {
        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert({
            id: authUser.id,
            email: authUser.email!,
            full_name: authUser.user_metadata?.full_name || authUser.email!.split('@')[0],
            role: 'NASABAH',
            is_active: false // Needs approval
          })
          .select('id, email, full_name, role, is_active')
          .single();

        if (insertError) throw insertError;
        userData = newUser;

        // Log the auto-registration
        await this.logActivity(userData.id, 'AUTO_REGISTER', 'User otomatis terdaftar via Google OAuth');
      } else if (userError) {
        throw userError;
      }

      if (!userData) {
        return { user: null, error: 'Data pengguna tidak ditemukan.' };
      }

      // Untuk NASABAH (khususnya via Google OAuth) pastikan baris
      // nasabah_profiles ada. Trigger DB seharusnya membuatnya, tetapi kita
      // self-heal di sisi klien agar alur pemilihan cabang & dashboard tidak
      // gagal dengan PGRST116 bila trigger tidak berjalan.
      if (userData.role === 'NASABAH') {
        await this.ensureNasabahProfile(userData.id);
      }

      return {
        user: {
          id: userData.id,
          email: userData.email,
          fullName: userData.full_name,
          role: userData.role as UserRole,
          isActive: userData.is_active
        },
        error: null
      };
    } catch (error) {
      return {
        user: null,
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Pastikan baris nasabah_profiles ada untuk user NASABAH.
   *
   * Cek keberadaan lebih dulu agar tidak membuat duplikat bila trigger DB
   * sudah membuat profil (branch_id NULL). branch_id sengaja dibiarkan NULL
   * supaya pengguna diarahkan memilih cabang saat pertama login.
   *
   * Cek memakai limit(1) (bukan maybeSingle) agar tetap aman bila data lama
   * sudah terlanjur punya lebih dari satu profil — jangan sampai malah menambah
   * baris ketiga. Best-effort: kegagalan tidak boleh menggagalkan alur login.
   */
  private async ensureNasabahProfile(userId: string): Promise<void> {
    try {
      const { data: existing, error: selError } = await supabase
        .from('nasabah_profiles')
        .select('id')
        .eq('user_id', userId)
        .limit(1);

      if (selError) throw selError;
      if (existing && existing.length > 0) return;

      // Tanpa cabang → account_number dibiarkan NULL; nomor terbit saat nasabah
      // memilih cabang (trigger DB migrasi 0052).
      const { error: insError } = await supabase
        .from('nasabah_profiles')
        .insert({
          user_id: userId,
          status: 'PENDING'
        });

      if (insError) throw insError;
    } catch (err) {
      console.error('Gagal memastikan profil nasabah:', err);
    }
  }

  /**
   * Get nasabah profile including branch_id
   */
  async getNasabahProfile(userId: string): Promise<{
    profile: {
      id: string;
      branchId: string | null;
      accountNumber: string;
      status: string;
    } | null;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('nasabah_profiles')
        .select('id, branch_id, account_number, status')
        .eq('user_id', userId)
        // Toleran terhadap data lama yang punya >1 profil untuk satu user:
        // utamakan yang sudah punya cabang (branch_id non-null), lalu terlama.
        .order('branch_id', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      // Profil belum ada → kembalikan null tanpa error (bukan PGRST116),
      // sehingga pemanggil bisa mengarahkan ke alur pemilihan cabang.
      if (!data) {
        return { profile: null, error: null };
      }

      return {
        profile: {
          id: data.id,
          branchId: data.branch_id,
          accountNumber: data.account_number,
          status: data.status
        },
        error: null
      };
    } catch (error) {
      return {
        profile: null,
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Validate branch exists and is active
   */
  async validateBranch(branchId: string): Promise<{
    valid: boolean;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('branches')
        .select('id, is_active')
        .eq('id', branchId)
        .single();

      if (error) {
        return { valid: false, error: 'Cabang tidak ditemukan' };
      }

      if (!data.is_active) {
        return { valid: false, error: 'Cabang tidak aktif' };
      }

      return { valid: true, error: null };
    } catch (error) {
      return {
        valid: false,
        error: 'Gagal memvalidasi cabang'
      };
    }
  }

  /**
   * Update branch_id in nasabah_profiles
   */
  async updateNasabahBranch(userId: string, branchId: string): Promise<{
    success: boolean;
    error: string | null;
  }> {
    try {
      // Validate branch exists and is active
      const { valid, error: validationError } = await this.validateBranch(branchId);
      if (!valid) {
        return { success: false, error: validationError };
      }

      // Nasabah TIDAK punya izin RLS untuk UPDATE nasabah_profiles miliknya
      // (np_manage hanya untuk staf cabang/super admin). Gunakan RPC
      // SECURITY DEFINER `set_my_nasabah_branch` (migrasi 0051) yang hanya
      // mengubah branch_id milik profil pemanggil — aman dari perubahan
      // balance/status.
      const { error } = await supabase.rpc('set_my_nasabah_branch', {
        p_branch_id: branchId,
      });
      if (error) throw error;

      // Verify update success
      const { data: updated } = await supabase
        .from('nasabah_profiles')
        .select('branch_id')
        .eq('user_id', userId)
        .single();

      if (updated?.branch_id !== branchId) {
        throw new Error('Verifikasi update gagal');
      }

      return { success: true, error: null };
    } catch (error) {
      return {
        success: false,
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Catat last_login_at/ip/device (RPC migrasi 0040) & reset counter gagal.
   * Best-effort: kegagalan tidak boleh menggagalkan alur login.
   */
  private async updateLoginTracking(): Promise<void> {
    try {
      const { error } = await supabase.rpc('update_login_tracking');
      if (error) throw error;
    } catch (err) {
      console.error('Gagal mencatat info login:', err);
    }
  }

  /**
   * Catat percobaan login gagal (RPC migrasi 0040). Dipanggil sebelum ada
   * sesi (auth.uid() belum ada), sehingga RPC mencari user berdasar email.
   * Best-effort: tidak boleh menutupi pesan error login asli.
   */
  private async recordFailedLogin(email: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('record_failed_login', { p_email: email });
      if (error) throw error;
    } catch (err) {
      console.error('Gagal mencatat percobaan login gagal:', err);
    }
  }

  /**
   * Log user activity
   */
  private async logActivity(userId: string, activityType: string, description: string): Promise<void> {
    try {
      // RPC log_activity (migrasi 0022) merekam IP address & perangkat dari
      // header request di server — tidak bisa dipalsukan payload klien.
      const { error } = await supabase.rpc('log_activity', {
        p_activity_type: activityType,
        p_description: description,
      });
      if (error) throw error;
    } catch {
      // Fallback bila RPC belum tersedia (migrasi 0022 belum dijalankan).
      try {
        await supabase.from('activity_logs').insert({
          user_id: userId,
          activity_type: activityType,
          description: description
        });
      } catch (fallbackError) {
        console.error('Failed to log activity:', fallbackError);
      }
    }
  }
}

export const authService = new AuthService();
